/* HTTP Today aggregate — GET /trips/:id/today. */
import { apiGet } from './httpClient'
import type { TodayData } from './types'
import { readTripSnapshot } from '../lib/offlineStore'
import { localDateIso } from '../lib/dates'

export async function getToday(tripId: string): Promise<TodayData> {
  try { return await apiGet<TodayData>(`/trips/${tripId}/today`) }
  catch (error) {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      const saved = await readTripSnapshot(tripId)
      if (saved?.points && saved.trip.start_date) {
        const now = new Date()
        const today = localDateIso(now, saved.trip.time_zone_id)
        const start = saved.trip.start_date
        const active = today >= start && (!saved.trip.end_date || today <= saved.trip.end_date)
        const assigned = saved.points.filter(p => p.day_index >= 0).map(p => p.day_index)
        const index = active ? Math.round((Date.parse(`${today}T12:00:00Z`) -
          Date.parse(`${start}T12:00:00Z`)) / 86_400_000) :
          (assigned.length ? Math.min(...assigned) : 0)
        const dayPoints = saved.points.filter(p => p.day_index === index)
          .sort((a,b) => (a.time ? 0 : 1) - (b.time ? 0 : 1) ||
            (a.time ?? '').localeCompare(b.time ?? '') || a.order-b.order)
        const dayIds = new Set(dayPoints.map(p => p.id))
        const visited = dayPoints.filter(p => p.status === 'visited')
        const localClock = new Intl.DateTimeFormat('en-GB', { timeZone: saved.trip.time_zone_id || 'Etc/UTC',
          hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(now)
        const minute = Number(localClock.slice(0,2))*60 + Number(localClock.slice(3,5))
        const pending = dayPoints.filter(p => p.status === 'planned' || p.status === 'changed')
        const inProgress = pending.find(p => p.time && Number(p.time.slice(0,2))*60 + Number(p.time.slice(3,5)) <= minute &&
          minute < Number(p.time.slice(0,2))*60 + Number(p.time.slice(3,5)) + (p.duration_minutes ?? 60))
        const future = pending.find(p => p.time && Number(p.time.slice(0,2))*60 + Number(p.time.slice(3,5)) > minute)
        const current = active ? inProgress ?? visited[visited.length-1] ?? null : null
        const next = active ? future ?? (inProgress ? null : pending[0] ?? null) : null
        return { current_point: current, next_point: next, today_points: dayPoints,
          today_documents: saved.documents.filter(d => !d.point_id || dayIds.has(d.point_id)) }
      }
      if (saved?.today) return saved.today
    }
    throw error
  }
}
